import { useState } from "react";
import { IconButton, ListItemIcon, Menu, MenuItem, Tooltip } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import ViewQuiltIcon from "@mui/icons-material/ViewQuilt";
import { useTranslation } from "react-i18next";
import { FormView } from "../services/formLayoutBuilder";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { RecordManagement } from "app/class/record-management";
import { resolveSectionTitle } from "./RecordFormSection";

export function FormViewSelector({ views, value, onChange, tableName }: {
    views: FormView[];
    value: string | null;
    onChange: (id: string | null) => void;
    tableName?: string;
}) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const access = useQuery({ queryKey: ["form-builder", "access"], enabled: Boolean(tableName), retry: false,
        queryFn: () => new RecordManagement("sys_form_views").fieldPermissions("update") });
    const canEdit = access.data?.permissions?.update === true;
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    if (!views.length) return null;

    const label = String(t("form_layout.view.selector", { defaultValue: "View" }));
    const selectView = (id: string | null) => {
        setAnchorEl(null);
        onChange(id);
    };
    const option = (id: string | null, title: string) => (
        <MenuItem
            key={id ?? "default"}
            data-testid={`form-view-option-${id ?? "default"}`}
            selected={value === id}
            onClick={() => selectView(id)}
        >
            <ListItemIcon>{value === id ? <CheckIcon fontSize="small" /> : null}</ListItemIcon>
            {title}
        </MenuItem>
    );

    return <>
        <Tooltip title={label}>
            <IconButton
                color="inherit"
                data-testid="form-view-selector"
                aria-label={label}
                aria-haspopup="menu"
                aria-controls={anchorEl ? "form-view-menu" : undefined}
                aria-expanded={Boolean(anchorEl)}
                onClick={event => setAnchorEl(event.currentTarget)}
                sx={{ width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
            >
                <ViewQuiltIcon fontSize="small" />
            </IconButton>
        </Tooltip>
        <Menu id="form-view-menu" anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
            {option(null, String(t("form_layout.view.default", { defaultValue: "Default" })))}
            {views.map(view => {
                const title = resolveSectionTitle(t, view.label) ?? view.number_ref ?? view.id;
                const source = view.inherited_from_table && (resolveSectionTitle(t, view.inherited_from_table_label ?? "") ?? view.inherited_from_table);
                return option(view.id, source
                    ? `${title} · ${t("form_layout.view.from_table", { defaultValue: "Da tabela {{table}}", table: source })}`
                    : title);
            })}
            {canEdit && tableName && <MenuItem onClick={() => { setAnchorEl(null); const chosen = views.find(view => view.id === (value ?? views.find(view => view.is_default)?.id ?? views[0].id)); navigate(chosen?.inherited_from_table ? `/home/form-builder/${tableName}` : `/home/form-builder/${tableName}/${chosen?.id ?? views[0].id}`); }}>
                {String(t("form_layout.builder.edit_form", { defaultValue: "Edit form" }))}
            </MenuItem>}
        </Menu>
    </>;
}
